import React, {useState} from 'react'
import {useTownhallTemplate} from 'Event/template/Townhall'
import {useLoadFont} from 'lib/FontSelect'
import {Editable} from 'Event/Dashboard/editor/views/EditComponent'
import Published from 'Event/Dashboard/editor/views/Published'
import VisibleOnMatch from 'Event/attendee-rules/VisibleOnMatch'
import Scheduled from 'lib/ui/layout/Scheduled'
import {GridProps, GridSize} from '@material-ui/core/Grid'
import {HashMap} from 'lib/list'
import styled from 'styled-components'
import {EditPost} from 'Event/template/Townhall/Dashboard/Main/BlogSection/BlogPostConfig'
import BlogPost from 'Event/template/Townhall/Dashboard/Main/BlogSection/BlogPost'
import {
  BlogSectionProps,
  TownhallBlogPost,
} from 'Event/template/Townhall/Dashboard/Main/BlogSection'
import AddBlogPostButton from 'Event/template/Townhall/Dashboard/Main/BlogSection/AddBlogPostButton'
import EditModeOnly from 'Event/Dashboard/editor/views/EditModeOnly'
import {getSortedIds} from 'Event/Dashboard/components/BlogPosts'

export default function BlogPostList(
  props: BlogSectionProps & {sectionId: string},
) {
  const template = useTownhallTemplate()
  const {postStyles, textColor} = template

  const [editing, setEditing] = useState<string | null>(null)

  useLoadFont(postStyles.titleFont)

  return (
    <Box>
      <EditPost {...props} id={editing} onClose={() => setEditing(null)} />
      <Row
        level={1}
        md={12}
        sm={12}
        xs={12}
        {...props}
        onSelectPost={setEditing}
        textColor={textColor}
      />
      <Row
        level={2}
        md={6}
        sm={6}
        xs={12}
        {...props}
        onSelectPost={setEditing}
        textColor={textColor}
      />
      <Row
        level={3}
        md={4}
        sm={6}
        xs={12}
        {...props}
        onSelectPost={setEditing}
        textColor={textColor}
      />
      <Row
        level={4}
        md={3}
        sm={6}
        xs={12}
        {...props}
        onSelectPost={setEditing}
        textColor={textColor}
      />
      <EditModeOnly>
        <StyledAddBlogPostButton {...props} />
      </EditModeOnly>
    </Box>
  )
}

function Row(
  props: Omit<GridProps, 'color'> &
    BlogSectionProps & {
      sectionId: string
      level: number
      onSelectPost: (editing: string | null) => void
      textColor: string
    },
) {
  return (
    <RowContainer>
      <Content {...props} />
    </RowContainer>
  )
}

function Content(
  props: Omit<GridProps, 'color'> &
    BlogSectionProps & {
      sectionId: string
      level: number
      onSelectPost: (editing: string | null) => void
      textColor: string
    },
) {
  const {
    items,
    level,
    onSelectPost,
    xs,
    sm,
    md,
    sectionId,
    postStyles,
    postFormStyles,
    textColor,
  } = props

  const levelPostIds = Object.keys(items).filter((id) => {
    const post = items[id]
    return post.level === level
  })

  const levelPosts = levelPostIds.reduce((acc, i) => {
    acc[i] = items[i]
    return acc
  }, {} as HashMap<TownhallBlogPost>)

  const sortedIds = getSortedIds(levelPosts)

  return (
    <PostGrid xs={xs} sm={sm} md={md} gap={postStyles?.spacing}>
      {sortedIds.map((id) => {
        const post = items[id]
        return (
          <Blog
            id={id}
            sectionId={sectionId}
            post={post}
            onSelectPost={onSelectPost}
            postStyles={postStyles}
            postFormStyles={postFormStyles}
            textColor={textColor}
          />
        )
      })}
    </PostGrid>
  )
}

function Blog(
  props: Omit<GridProps, 'color'> & {
    id: string
    sectionId: string
    post: TownhallBlogPost
    postStyles?: BlogSectionProps['postStyles']
    postFormStyles?: BlogSectionProps['postFormStyles']
    onSelectPost: (editing: string | null) => void
    textColor: string
  },
) {
  const {
    post,
    id,
    sectionId,
    postStyles,
    postFormStyles,
    onSelectPost,
    textColor,
  } = props

  useLoadFont(postStyles?.titleFont)

  return (
    <Published component={post}>
      <VisibleOnMatch rules={post.rules}>
        <Scheduled
          component={{
            showingFrom: post.publishAt,
            showingUntil: post.showingUntil,
          }}
        >
          <div style={{minWidth: 0}} key={id}>
            <Editable
              key={id}
              aria-label="edit blog post"
              onEdit={() => onSelectPost(id)}
            >
              <BlogPost
                post={post}
                postId={id}
                sectionId={sectionId}
                postStyles={postStyles}
                postFormStyles={postFormStyles}
                onOpen={() => onSelectPost(id)}
                textColor={textColor}
              />
            </Editable>
          </div>
        </Scheduled>
      </VisibleOnMatch>
    </Published>
  )
}

const calculateColumns = (size?: GridSize | boolean) => {
  if (typeof size === 'boolean') return 12
  if (typeof size === 'number') return Math.max(1, Math.round(12 / size))
  return 12
}

const PostGrid = styled.div<{
  xs?: GridSize | boolean
  sm?: GridSize | boolean
  md?: GridSize | boolean
  gap?: number
}>`
  display: grid;
  width: 100%;
  box-sizing: border-box;
  gap: ${(p) => `max(${p.gap ?? 0}px, ${p.theme.spacing[2]})`};
  grid-template-columns: repeat(${(p) => calculateColumns(p.xs)}, 1fr);

  @media (min-width: ${(p) => p.theme.breakpoints.sm}) {
    grid-template-columns: repeat(${(p) => calculateColumns(p.sm)}, 1fr);
  }

  @media (min-width: ${(p) => p.theme.breakpoints.md}) {
    grid-template-columns: repeat(${(p) => calculateColumns(p.md)}, 1fr);
  }
`

const Box = styled.div`
  width: 100%;
`

const RowContainer = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[3]};
`

const StyledAddBlogPostButton = styled(AddBlogPostButton)`
  margin-top: ${(props) => props.theme.spacing[3]};
  margin-bottom: ${(props) => props.theme.spacing[3]};
`
